import React, { useState, useEffect, useRef } from 'react';
import { Sync, PlayArrow, MoreVert } from '@mui/icons-material';
import { DynamicContainer } from './common/DynamicContainer';
import { generateBreadcrumbPath } from './common/breadcrumbs';
import { toast } from 'react-toastify';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Menu,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';
import HubFlowParameter from './HubFlowDrawer/HubFlowParameter';
import DatahubTrackLogModal from './DatahubTrackLogModal';
import { withBrowserTimezoneHeaders } from '../utils/browserTimezone';
import { useUserRights } from '../context/UserRightsContext';
import {
  isMfUiActionEnabled,
  uiConfigForMasterFlow,
} from '../services/masterFlowUiConfig';
import { fetchTenantLicensedFlowUi } from '../services/licensingApi';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-instances`;
const HUB_FLOW_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/hub-flows`;
const MASTER_FLOW_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow`;
const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;
const CONTAINER_ASSIGNMENTS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-container-assignments`;
const TRANSFORMATION_HEADER_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/transformation-header`;
const SYNC_FLOW_PROPERTIES_API = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`;
const TENANT_AGENTS_API = `${process.env.REACT_APP_BACKEND_URL}asrosync/agents/tenant`;

const PLANNER_FLOW_ENDPOINT = '/synctrack/agent/planner/execute';
const EXECUTOR_FLOW_ENDPOINT = '/synctrack/agent/executor/execute';
const TARGET_AGENT_PARAM = 'TargetAgentId';

const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');


// Trigger Type Selector Component
const TriggerTypeSelector = ({ value, onChange, error }) => {
  const parseInitialValue = () => ({
    triggerType: value?.triggerType || 'Manual',
    intervalValue: value?.intervalValue || '',
    timeValue: value?.timeValue || '',
  });

  const [state, setState] = useState(parseInitialValue());

  useEffect(() => {
    setState(parseInitialValue());
  }, [value]);

  const handleTriggerChange = (event) => {
    const newTriggerType = event.target.value;
    setState((prev) => ({ ...prev, triggerType: newTriggerType }));
    onChange({
      triggerType: newTriggerType,
      intervalValue: state.intervalValue,
      timeValue: state.timeValue,
    });
  };

  const handleIntervalChange = (event) => {
    const newInterval = event.target.value;
    setState((prev) => ({ ...prev, intervalValue: newInterval }));
    onChange({
      triggerType: state.triggerType,
      intervalValue: newInterval,
      timeValue: state.timeValue,
    });
  };

  const handleTimeChange = (event) => {
    const newTime = event.target.value;
    setState((prev) => ({ ...prev, timeValue: newTime }));
    onChange({
      triggerType: state.triggerType,
      intervalValue: state.intervalValue,
      timeValue: newTime,
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FormControl fullWidth margin="normal" error={!!error}>
        <InputLabel id="trigger-type-label">Trigger Type</InputLabel>
        <Select
          labelId="trigger-type-label"
          value={state.triggerType || 'Manual'}
          label="Trigger Type"
          onChange={handleTriggerChange}
        >
          <MenuItem value="Manual">Manual</MenuItem>
          <MenuItem value="Minutes">Minutes</MenuItem>
          <MenuItem value="Time">Time</MenuItem>
        </Select>
      </FormControl>
      {state.triggerType === 'Minutes' && (
        <TextField
          fullWidth
          margin="normal"
          label="Interval (minutes)"
          type="number"
          value={state.intervalValue}
          onChange={handleIntervalChange}
          inputProps={{ min: 1 }}
        />
      )}
      {state.triggerType === 'Time' && (
        <TextField
          fullWidth
          margin="normal"
          label="Time (HH:MM)"
          type="time"
          value={state.timeValue}
          onChange={handleTimeChange}
          InputLabelProps={{ shrink: true }}
        />
      )}
    </Box>
  );
};

// Normalize raw status values (from sfi_status or srs_status) to the four
// canonical UPPERCASE statuses shown on the Hub Flow grid.
const normalizeHubFlowStatus = (rawStatus) => {
  if (!rawStatus) return 'NOT STARTED';
  switch (rawStatus.toLowerCase().trim()) {
    case 'not started':
      return 'NOT STARTED';
    case 'running':
      return 'RUNNING';
    case 'completed':
    case 'success':
      return 'SUCCESS';
    case 'error':
    case 'failed':
      return 'FAILED';
    default:
      return rawStatus.toUpperCase();
  }
};

const HubFlow = () => {
  const { hasFullAccess, canExecute } = useUserRights();
  const allowExecute = hasFullAccess || canExecute('Hub Flow');
  const [hubFlows, setHubFlows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [masterFlowsData, setMasterFlowsData] = useState([]);
  const [masterFlowOptions, setMasterFlowOptions] = useState([]); // For Flow dropdown (Master Flows)
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [sourceConnectorOptions, setSourceConnectorOptions] = useState([]);
  const [targetConnectorOptions, setTargetConnectorOptions] = useState([]); // For Target Connector dropdown
  const [selectedFlowDetails, setSelectedFlowDetails] = useState(null);
  const [paramDrawerOpen, setParamDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [trackLogModalOpen, setTrackLogModalOpen] = useState(false);
  const [selectedFlowForTrackLog, setSelectedFlowForTrackLog] = useState(null);
  const [schemaCompareOpen, setSchemaCompareOpen] = useState(false);
  const [schemaCompareLoading, setSchemaCompareLoading] = useState(false);
  const [schemaCompareReport, setSchemaCompareReport] = useState(null);
  const [schemaCompareMode, setSchemaCompareMode] = useState('view'); // 'view' | 'confirm-run'
  const [pendingRunFlow, setPendingRunFlow] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSourceConnectorType, setSelectedSourceConnectorType] = useState(''); // Track source connector type name
  const [runningFlowIds, setRunningFlowIds] = useState(new Set()); // Track running flows
  const [executingFlow, setExecutingFlow] = useState(false); // Track if any flow is executing
  const previousFlowStatusesRef = useRef(new Map()); // Track previous flow statuses to detect completion
  const statusPollIntervalRef = useRef(null);
  const statusPollInFlightRef = useRef(false);
  const [tenantAgents, setTenantAgents] = useState([]);
  const [licensedFlowUiByMfId, setLicensedFlowUiByMfId] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tenantId || tenantId === 'ALL') {
        setLicensedFlowUiByMfId({});
        return;
      }
      try {
        const byMfId = await fetchTenantLicensedFlowUi(tenantId);
        if (!cancelled) setLicensedFlowUiByMfId(byMfId || {});
      } catch {
        if (!cancelled) setLicensedFlowUiByMfId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    fetchHubFlows();
    fetchMasterFlows();
    fetchMasterConnectors();
    fetchCategories();
    fetchTenantAgents();
    return () => {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory);
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory]);

  const fetchTenantAgents = async () => {
    try {
      const response = await fetch(`${TENANT_AGENTS_API}/${tenantId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const agents = await response.json();
        setTenantAgents(
          (Array.isArray(agents) ? agents : []).filter(
            (a) => a.agentName || a.tenantAgentInstanceId || a.agentUniqueId
          )
        );
      }
    } catch (error) {
      console.error('Error fetching tenant agents:', error);
    }
  };

  const isPlannerFlowByName = (flowName) => {
    if (!flowName || !masterFlowsData.length) return false;
    const masterFlow = masterFlowsData.find(
      (flow) =>
        flow.mfFlowFunction?.toLowerCase() === flowName?.toLowerCase() ||
        flow.mfName?.toLowerCase() === flowName?.toLowerCase()
    );
    return masterFlow?.mfFlowFunction === PLANNER_FLOW_ENDPOINT;
  };

  const isAgentFlowByName = (flowName) => {
    if (!flowName || !masterFlowsData.length) return false;
    const masterFlow = masterFlowsData.find(
      (flow) =>
        flow.mfFlowFunction?.toLowerCase() === flowName?.toLowerCase() ||
        flow.mfName?.toLowerCase() === flowName?.toLowerCase()
    );
    const endpoint = masterFlow?.mfFlowFunction;
    return endpoint === PLANNER_FLOW_ENDPOINT || endpoint === EXECUTOR_FLOW_ENDPOINT;
  };

  const buildTargetAgentOptions = () => {
    const options = [{ value: '', label: 'All Agents' }];
    tenantAgents.forEach((a) => {
      const value = a.tenantAgentInstanceId ?? a.agentUniqueId ?? a.agentName;
      const label = a.agentName || value;
      if (value) {
        options.push({ value: String(value), label: String(label) });
      }
    });
    return options;
  };

  const saveTargetAgentProperty = async (masterFlowId, instanceId, agentId) => {
    const response = await fetch(SYNC_FLOW_PROPERTIES_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify([
        {
          sfpMfId: masterFlowId,
          sfpInstanceId: instanceId,
          sfpName: TARGET_AGENT_PARAM,
          sfpValue: agentId || '',
          sfpMcId: null,
        },
      ]),
    });
    if (!response.ok) {
      throw new Error('Failed to save target agent');
    }
  };

  const loadTargetAgentProperty = async (masterFlowId, instanceId) => {
    if (!masterFlowId || !instanceId) return '';
    try {
      const response = await fetch(
        `${SYNC_FLOW_PROPERTIES_API}/${masterFlowId}?instanceId=${instanceId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) return '';
      const data = await response.json();
      const row = Array.isArray(data)
        ? data.find((p) => p.sfpName === TARGET_AGENT_PARAM)
        : null;
      return row?.sfpValue?.trim() || '';
    } catch (error) {
      console.error('Error loading target agent property:', error);
      return '';
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${TRANSFORMATION_HEADER_API_URL}/datahub/categories`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Validate category against connector type (fallback: connector name).
  const validateCategoryAgainstSourceConnector = (category, sourceConnectorValue) => {
    // If category is empty or "All", no validation needed
    if (!category || category === '' || category === 'All') {
      return;
    }

    // Get selected source connector metadata.
    const selectedConnectorOption = sourceConnectorOptions.find(
      opt => opt.value === sourceConnectorValue || opt.value?.toString() === sourceConnectorValue?.toString()
    );

    if (!selectedConnectorOption) {
      // Source connector not selected yet, skip validation
      return;
    }

    const connectorTypeForValidation =
      selectedConnectorOption.mcConnectorType || selectedConnectorOption.mcName;
    if (!connectorTypeForValidation) return;

    // Normalize names for comparison (case-insensitive, remove spaces/special chars)
    const normalize = (str) => {
      if (!str) return '';
      return str.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    };

    const normalizedCategory = normalize(category);
    const normalizedConnectorType = normalize(connectorTypeForValidation);

    // DB discovery Hub: Category "Database Sync" with Postgres/Snowflake source is intentional.
    const isDbSyncCategory =
      normalizedCategory.includes('databasesync') ||
      normalizedCategory.includes('dbsync') ||
      normalizedCategory.includes('schemasync') ||
      normalizedCategory.includes('postgrestosnowflake');
    const isDbConnector =
      normalizedConnectorType.includes('postgres') ||
      normalizedConnectorType.includes('snowflake');
    if (isDbSyncCategory && isDbConnector) {
      return;
    }

    // Also try simple case-insensitive comparison
    const simpleMatch = category.toLowerCase().includes(connectorTypeForValidation.toLowerCase()) ||
      connectorTypeForValidation.toLowerCase().includes(category.toLowerCase());

    // Check if normalized category contains connector type or vice versa
    const categoryContainsConnector = normalizedCategory.includes(normalizedConnectorType);
    const connectorContainsCategory = normalizedConnectorType.includes(normalizedCategory);

    // If neither simple match nor normalized match, show warning
    if (!simpleMatch && !categoryContainsConnector && !connectorContainsCategory) {
      // Show warning if they don't match
      toast.warning(
        `Warning: Category "${category}" doesn't match source connector "${connectorTypeForValidation}". ` +
        `The flow will process APIs in this category, but ensure the connector assignment is correct.`,
        { autoClose: 5000 }
      );
    }
  };

  const fetchSubCategories = async (category) => {
    try {
      const response = await fetch(`${TRANSFORMATION_HEADER_API_URL}/datahub/sub-categories?category=${encodeURIComponent(category)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setSubCategories([]);
    }
  };

  const fetchHubFlows = async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/group/HubFlow`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Data is already filtered by backend to only include HubFlow group
      const hubFlowData = data;

      // Fetch connectors for each flow
      // Map Hub Flow data without fetching connectors (connectors are not displayed in grid)
      const mappedData = hubFlowData.map((item) => {
        return {
          id: item.recId || '',
          sfl_id: item.recId || '',
          sfl_description: item.sfiDescription || '',
          status: normalizeHubFlowStatus(item.sfiStatus || 'Not Started'),
          instanceId: item.sfiId || '',
          sfiMfId: item.sfiMfId || '', // Master flow ID for getting master flow name
          sfiTriggerType: item.sfiTriggerType || 'Manual',
          sfiInterval: item.sfiInterval || '',
          sfiTime: item.sfiTime || '',
          sfiCategory: item.sfiCategory || '',
          sfiSubCategory: item.sfiSubCategory || '',
          sfiSourceConnectorId: item.sfiSourceConnectorId || null, // Source connector ID from database
          sfiTargetConnectorId: item.sfiTargetConnectorId || null, // Target connector ID from database
        };
      });

      // Check for status changes to show completion toast messages
      mappedData.forEach(flow => {
        const previousStatus = previousFlowStatusesRef.current.get(flow.instanceId)?.toLowerCase();
        const currentStatus = flow.status?.toLowerCase();

        // Detect status change from Running to terminal state
        const terminal =
          currentStatus === 'completed' ||
          currentStatus === 'success' ||
          currentStatus === 'failed' ||
          currentStatus === 'error' ||
          currentStatus === 'success with errors';
        if (previousStatus === 'running' && terminal) {
          // Grid `status` is enriched from sync_run_status (e.g. failed, success with errors) or sfi_status (Completed/Error).
          if (currentStatus === 'success with errors' || currentStatus === 'failed' || currentStatus === 'error') {
            toast.error(`Hub Flow execution: ${flow.status || currentStatus}`);
          } else if (currentStatus === 'completed' || currentStatus === 'success') {
            toast.success('Hub Flow executed successfully');
          } else {
            toast.error(`Hub Flow execution: ${flow.status || currentStatus}`);
          }
        }

        // Update previous status
        previousFlowStatusesRef.current.set(flow.instanceId, flow.status);
      });

      setHubFlows(mappedData);

      // Check if any running flows have completed and remove them from runningFlowIds
      setRunningFlowIds(prev => {
        const newSet = new Set(prev);
        let hasChanges = false;

        mappedData.forEach(flow => {
          if (newSet.has(flow.instanceId)) {
            // Check if flow status is no longer running
            const status = flow.status?.toLowerCase();
            if (
              status === 'completed' ||
              status === 'success' ||
              status === 'failed' ||
              status === 'error' ||
              status === 'success with errors'
            ) {
              newSet.delete(flow.instanceId);
              hasChanges = true;
            }
          }
        });

        // If all flows completed, stop executing
        if (newSet.size === 0 && executingFlow) {
          setExecutingFlow(false);
        }
        if (newSet.size === 0 && statusPollIntervalRef.current) {
          clearInterval(statusPollIntervalRef.current);
          statusPollIntervalRef.current = null;
        }

        return newSet;
      });
      return mappedData;
    } catch (error) {
      console.error('Error fetching hub flows:', error);
      if (!silent) {
        toast.error(`Error loading hub flows: ${error.message}`);
      }
      return null;
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const fetchMasterFlows = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(MASTER_FLOW_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMasterFlowsData(data);
        // Create options for dropdown (same as Sync Flow page)
        const options = data.map((flow) => flow.mfName || flow.mfName).filter(Boolean);
        setMasterFlowOptions(options);
      }
    } catch (error) {
      console.error('Error fetching master flows:', error);
      toast.error(`Error loading master flows: ${error.message}`);
    }
  };

  const getMasterFlowIdByName = (masterFlowName) => {
    const masterFlow = masterFlowsData.find(
      (flow) =>
        (flow.mfFlowFunction?.toLowerCase() === masterFlowName?.toLowerCase()) ||
        (flow.mfName?.toLowerCase() === masterFlowName?.toLowerCase())
    );
    return masterFlow ? masterFlow.mfId : null;
  };

  const getMasterFlowNameById = (masterFlowId) => {
    if (!masterFlowId) return '';
    // Handle both UUID and string comparison
    const masterFlow = masterFlowsData.find((flow) =>
      flow.mfId === masterFlowId ||
      flow.mfId?.toString() === masterFlowId?.toString() ||
      flow.mfId?.toString() === String(masterFlowId)
    );
    return masterFlow ? (masterFlow.mfName || masterFlow.mfFlowFunction) : '';
  };

  const findMasterConnectorById = (connectorId) => {
    if (!connectorId) return null;
    const connectorIdStr = connectorId.toString();
    return masterConnectors.find((conn) => conn.mcId?.toString() === connectorIdStr) || null;
  };

  const getConnectorMatchKey = (value) => {
    if (!value) return '';
    const normalized = String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized.includes('quickbook')) return 'quickbook';
    if (normalized.includes('salesforce')) return 'salesforce';
    if (normalized.includes('fusion')) return 'fusion';
    if (normalized.includes('autodeskdocs')) return 'autodeskdocs';
    if (normalized.includes('autodesk')) return 'autodesk';
    if (normalized.includes('postgres')) return 'postgres';
    if (normalized.includes('snowflake')) return 'snowflake';
    return normalized;
  };

  const resolveMasterConnectorMeta = async (connectorId) => {
    if (!connectorId) return null;

    const masterFromId = findMasterConnectorById(connectorId);
    if (masterFromId?.mcName || masterFromId?.mcConnectorType) {
      return {
        mcName: masterFromId.mcName || null,
        mcConnectorType: masterFromId.mcConnectorType || null,
      };
    }

    // Backward compatibility: older rows may still store sync connector IDs.
    try {
      const syncConnectorResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/${connectorId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!syncConnectorResponse.ok) return null;
      const syncConnector = await syncConnectorResponse.json();
      const masterFromSync = findMasterConnectorById(syncConnector?.mcId);
      if (!masterFromSync) return null;
      return {
        mcName: masterFromSync.mcName || null,
        mcConnectorType: masterFromSync.mcConnectorType || null,
      };
    } catch (error) {
      console.error('Error resolving connector type from ID:', error);
      return null;
    }
  };

  const resolveMasterConnectorType = async (connectorId) => {
    const connectorMeta = await resolveMasterConnectorMeta(connectorId);
    return connectorMeta?.mcName || null;
  };

  const fetchMasterConnectors = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(MASTER_CONNECTOR_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        const connectors = Array.isArray(data) ? data : [];
        setMasterConnectors(connectors);

        const isTargetMaster = (name = '', type = '') => {
          const normalizedName = String(name).trim().toLowerCase();
          const normalizedType = String(type).trim().toLowerCase();
          return (
            normalizedName.includes('postgres') ||
            normalizedName.includes('snowflake') ||
            normalizedType.includes('postgres') ||
            normalizedType.includes('snowflake')
          );
        };

        // Source includes all masters (API systems + Postgres/Snowflake for DB discovery sync).
        const sourceOptions = connectors
          .filter((conn) => conn.mcId && conn.mcName)
          .map((conn) => ({
            value: conn.mcId.toString(),
            label: conn.mcName,
            mcName: conn.mcName,
            mcConnectorType: conn.mcConnectorType,
          }));

        const targetOptions = connectors
          .filter((conn) => conn.mcId && conn.mcName && isTargetMaster(conn.mcName, conn.mcConnectorType))
          .map((conn) => ({
            value: conn.mcId.toString(),
            label: conn.mcName,
            mcName: conn.mcName,
            mcConnectorType: conn.mcConnectorType,
          }));

        setSourceConnectorOptions(sourceOptions);
        setTargetConnectorOptions(targetOptions);
      }
    } catch (error) {
      console.error('Error fetching master connectors:', error);
      toast.error(`Error loading connectors: ${error.message}`);
    }
  };

  const handleCreate = async (data) => {
    try {
      // Get master flow ID from selected flow name (same as Sync Flow page)
      const masterFlowId = getMasterFlowIdByName(data.flow);
      if (!masterFlowId) {
        throw new Error('Invalid master flow selection');
      }

      // Get master flow name for description
      const masterFlowName = data.flow || '';

      // Store selected master connector ID.
      const sourceConnectorId = data.sourceConnector && data.sourceConnector.toString().trim() !== ''
        ? data.sourceConnector.toString().trim()
        : null;

      // Debug: Log the selected connector
      const selectedConnectorOption = sourceConnectorOptions.find(opt => opt.value === data.sourceConnector || opt.value?.toString() === data.sourceConnector?.toString());
      console.log('handleCreate - Selected connector:', {
        rawValue: data.sourceConnector,
        sourceConnectorId: sourceConnectorId,
        connectorOption: selectedConnectorOption,
        allOptions: sourceConnectorOptions.map(opt => ({ value: opt.value, label: opt.label, mcName: opt.mcName }))
      });

      // Store selected target master connector ID.
      const targetConnectorId = data.targetConnector && data.targetConnector.toString().trim() !== ''
        ? data.targetConnector.toString().trim()
        : null;

      const backendData = {
        sfiMfId: masterFlowId, // Use selected master flow ID
        sfiGroup: 'HubFlow', // Set internally - always 'HubFlow' for Hub Flow page
        sfiTriggerType: data.sfiTriggerType?.triggerType || 'Manual',
        sfiInterval: data.sfiTriggerType?.triggerType === 'Minutes'
          ? parseInt(data.sfiTriggerType?.intervalValue, 10) || null
          : null,
        sfiTime: data.sfiTriggerType?.triggerType === 'Time'
          ? data.sfiTriggerType?.timeValue || null
          : null,
        sfiDescription:
          (data.sfl_description && String(data.sfl_description).trim()) || masterFlowName || '',
        sfiStatus: 'Not Started',
        sfiCategory: (data.category && data.category.trim() !== '') ? data.category : null,
        sfiSubCategory: (data.subCategory && data.subCategory.trim() !== '') ? data.subCategory : null,
        sfiSourceConnectorId: sourceConnectorId, // Store source master connector ID (mc_id)
        sfiTargetConnectorId: targetConnectorId, // Store target master connector ID (mc_id)
      };

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(backendData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      const instanceId = responseData?.sfiId || responseData?.sfi_id;
      if (isPlannerFlowByName(data.flow) && instanceId) {
        await saveTargetAgentProperty(masterFlowId, instanceId, data.targetAgent || '');
      }

      toast.success('Hub Flow created successfully');
      await fetchHubFlows();
      return responseData;
    } catch (error) {
      console.error('Create error:', error);
      toast.error(`Error creating hub flow: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const hubFlow = hubFlows.find((flow) => flow.sfl_id.toString() === id.toString());
      if (!hubFlow || !hubFlow.instanceId) {
        throw new Error('Hub Flow instance not found');
      }

      // Get master flow ID from selected flow name (same as Sync Flow page)
      const masterFlowId = getMasterFlowIdByName(data.flow);
      if (!masterFlowId) {
        throw new Error('Invalid master flow selection');
      }

      // Store selected source master connector ID.
      const sourceConnectorId = data.sourceConnector && data.sourceConnector.toString().trim() !== ''
        ? data.sourceConnector.toString().trim()
        : null;

      // Debug: Log the selected connector
      const selectedConnectorOption = sourceConnectorOptions.find(opt => opt.value === data.sourceConnector || opt.value?.toString() === data.sourceConnector?.toString());
      console.log('handleUpdate - Selected connector:', {
        rawValue: data.sourceConnector,
        sourceConnectorId: sourceConnectorId,
        connectorOption: selectedConnectorOption,
        allOptions: sourceConnectorOptions.map(opt => ({ value: opt.value, label: opt.label, mcName: opt.mcName }))
      });

      // Store selected target master connector ID.
      const targetConnectorId = data.targetConnector && data.targetConnector.toString().trim() !== ''
        ? data.targetConnector.toString().trim()
        : null;

      const masterFlowName = data.flow || '';
      const description =
        (data.sfl_description && String(data.sfl_description).trim()) ||
        masterFlowName ||
        hubFlow.sfl_description ||
        '';

      const backendData = {
        sfiMfId: masterFlowId, // Use selected master flow ID
        sfiGroup: 'HubFlow', // Set internally - always 'HubFlow' for Hub Flow page
        sfiTriggerType: data.sfiTriggerType?.triggerType || 'Manual',
        sfiInterval: data.sfiTriggerType?.triggerType === 'Minutes'
          ? parseInt(data.sfiTriggerType?.intervalValue, 10) || null
          : null,
        sfiTime: data.sfiTriggerType?.triggerType === 'Time'
          ? data.sfiTriggerType?.timeValue || null
          : null,
        sfiDescription: description,
        sfiCategory: (data.category && data.category.trim() !== '') ? data.category : null,
        sfiSubCategory: (data.subCategory && data.subCategory.trim() !== '') ? data.subCategory : null,
        sfiSourceConnectorId: sourceConnectorId, // Store source master connector ID (mc_id)
        sfiTargetConnectorId: targetConnectorId, // Store target master connector ID (mc_id)
      };

      const response = await fetch(`${API_BASE_URL}/${hubFlow.instanceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(backendData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedInstance = await response.json();
      console.log('handleUpdate - Response from backend:', updatedInstance);

      if (isPlannerFlowByName(data.flow)) {
        await saveTargetAgentProperty(masterFlowId, hubFlow.instanceId, data.targetAgent || '');
      }

      toast.success('Hub Flow updated successfully');
      await fetchHubFlows();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(`Error updating hub flow: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const hubFlow = hubFlows.find((flow) => flow.sfl_id.toString() === id.toString());
      if (!hubFlow || !hubFlow.instanceId) {
        throw new Error('Hub Flow instance not found');
      }

      const response = await fetch(`${API_BASE_URL}/${hubFlow.instanceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Hub Flow deleted successfully');
      await fetchHubFlows();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Error deleting hub flow: ${error.message}`);
      throw error;
    }
  };

  const validateHubFlowConnectors = async (hubFlow) => {
    // Validate connectors are assigned (similar to SyncFlow validation)
    // HubFlow needs BOTH:
    // 1. Form selections: sfiSourceConnectorId and sfiTargetConnectorId (from HubFlow form)
    // 2. Connector assignments: entries in sync_flow_connector_assignment table (from Properties drawer)

    const masterFlowId = hubFlow.sfiMfId || hubFlow.masterFlowId;
    if (!masterFlowId) {
      console.warn('No master flow ID found, skipping connector assignment validation');
      return;
    }

    // Check if this is an agent flow (Planner or Executor) - these don't need connectors
    let isAgentFlow = false;
    let requiresTargetConnector = true;
    try {
      const masterFlowResponse = await fetch(`${MASTER_FLOW_API_URL}/${masterFlowId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (masterFlowResponse.ok) {
        const masterFlow = await masterFlowResponse.json();
        const flowEndpoint = masterFlow.mfFlowFunction;

        // Check if this is an agent flow
        isAgentFlow = flowEndpoint === '/synctrack/agent/planner/execute' ||
          flowEndpoint === '/synctrack/agent/executor/execute';

        requiresTargetConnector = masterFlow.mfnTargetAppId != null;
        console.log('Master flow endpoint:', flowEndpoint);
        console.log('Is agent flow:', isAgentFlow);
        console.log('Master flow requires target connector:', requiresTargetConnector);
      }
    } catch (error) {
      console.warn('Could not check master flow details:', error);
      // Default to requiring target connector if check fails
    }

    // Skip connector validation for agent flows (they don't need connectors)
    if (isAgentFlow) {
      console.log('✅ Agent flow detected - skipping connector validation');
      return;
    }

    // First, check if connectors are selected in the form
    const sourceConnectorId = hubFlow.sfiSourceConnectorId;
    const targetConnectorId = hubFlow.sfiTargetConnectorId;

    const missingFormConnectors = [];
    if (!sourceConnectorId ||
      sourceConnectorId === null ||
      sourceConnectorId === undefined ||
      sourceConnectorId === '' ||
      String(sourceConnectorId).trim() === '' ||
      String(sourceConnectorId).toLowerCase() === 'null') {
      missingFormConnectors.push('source');
    }

    // Only check target connector if it's required by the master flow
    if (requiresTargetConnector) {
      if (!targetConnectorId ||
        targetConnectorId === null ||
        targetConnectorId === undefined ||
        targetConnectorId === '' ||
        String(targetConnectorId).trim() === '' ||
        String(targetConnectorId).toLowerCase() === 'null') {
        missingFormConnectors.push('target');
      }
    }

    if (missingFormConnectors.length > 0) {
      const connectorNames = missingFormConnectors.map(c => c === 'source' ? 'source' : 'target').join(' and ');
      const errorMessage = `Select ${connectorNames} connector${missingFormConnectors.length > 1 ? 's' : ''} in the Hub Flow form before running this flow.`;
      console.error('❌ Missing connectors in form - throwing error:', errorMessage);
      throw new Error(errorMessage);
    }

    // Fetch connector assignments for this HubFlow instance
    const CONNECTOR_ASSIGNMENT_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-connector-assignment`;
    const url = hubFlow.instanceId
      ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}?instanceId=${hubFlow.instanceId}`
      : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}`;

    console.log('🔍 Validating Hub Flow connector assignments:', {
      instanceId: hubFlow.instanceId,
      masterFlowId,
      url
    });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const assignments = await response.json();
      console.log('📋 Connector assignments found:', assignments);

      // Check if we have at least one connector assignment
      if (!assignments || assignments.length === 0) {
        const errorMessage = 'Assign connector(s) before running this flow.';
        console.error('❌ No connector assignments found - throwing error:', errorMessage);
        throw new Error(errorMessage);
      }

      // Check if assignments have connector IDs
      const validAssignments = assignments.filter(a => a.connectorId && a.connectorType);
      if (validAssignments.length === 0) {
        const errorMessage = 'Assign connector(s) before running this flow.';
        console.error('❌ No valid connector assignments found - throwing error:', errorMessage);
        throw new Error(errorMessage);
      }

      // For HubFlow, we need source connector (and target connector if required)
      // Get the expected connector types from the form selections
      const sourceConnectorId = hubFlow.sfiSourceConnectorId;
      const targetConnectorId = hubFlow.sfiTargetConnectorId;

      // Resolve connector type names from stored IDs.
      const sourceConnectorMeta = await resolveMasterConnectorMeta(sourceConnectorId);
      const targetConnectorMeta = (requiresTargetConnector && targetConnectorId)
        ? await resolveMasterConnectorMeta(targetConnectorId)
        : null;

      // Check if required connectors are assigned
      const assignedConnectorKeys = new Set(
        validAssignments.map((a) => getConnectorMatchKey(a.connectorType))
      );
      const missingConnectors = [];

      const sourceExpectedKey = getConnectorMatchKey(
        sourceConnectorMeta?.mcConnectorType || sourceConnectorMeta?.mcName
      );
      if (sourceExpectedKey && !assignedConnectorKeys.has(sourceExpectedKey)) {
        missingConnectors.push(sourceConnectorMeta?.mcName || sourceConnectorMeta?.mcConnectorType || 'Source');
      }

      // Only check target connector if it's required
      const targetExpectedKey = getConnectorMatchKey(
        targetConnectorMeta?.mcConnectorType || targetConnectorMeta?.mcName
      );
      if (requiresTargetConnector && targetExpectedKey && !assignedConnectorKeys.has(targetExpectedKey)) {
        missingConnectors.push(targetConnectorMeta?.mcName || targetConnectorMeta?.mcConnectorType || 'Target');
      }

      if (missingConnectors.length > 0) {
        const errorMessage = `Assign connector(s) ${missingConnectors.join(', ')} before running this flow.`;
        console.error('❌ Missing connector assignments:', missingConnectors);
        throw new Error(errorMessage);
      }

      const requiredAssignmentKeys = new Map([
        [sourceExpectedKey, sourceConnectorMeta?.mcName || sourceConnectorMeta?.mcConnectorType || 'Source'],
      ]);
      if (requiresTargetConnector) {
        requiredAssignmentKeys.set(
          targetExpectedKey,
          targetConnectorMeta?.mcName || targetConnectorMeta?.mcConnectorType || 'Target'
        );
      }

      const disconnectedConnectors = validAssignments
        .filter((assignment) => requiredAssignmentKeys.has(getConnectorMatchKey(assignment.connectorType)))
        .filter((assignment) =>
          String(assignment.connectorStatus || '').trim().toLowerCase() !== 'connected'
        )
        .map((assignment) =>
          requiredAssignmentKeys.get(getConnectorMatchKey(assignment.connectorType))
        );

      if (disconnectedConnectors.length > 0) {
        const connectorNames = [...new Set(disconnectedConnectors)].join(', ');
        throw new Error(
          `${connectorNames} connector is assigned but Disconnected. `
          + 'Please reconnect it in the Connector Token screen before running this flow.'
        );
      }

      const requiredConnectors = requiresTargetConnector ? 'source and target' : 'source';
      console.log(`✅ Validation passed - ${requiredConnectors} connector(s) are assigned:`, validAssignments.map(a => a.connectorType));
    } catch (error) {
      // If it's already our validation error, re-throw it
      if (
        error.message
        && (error.message.includes('Assign connector') || error.message.includes('assigned but Disconnected'))
      ) {
        throw error;
      }
      // For other errors (network, etc.), show a generic message
      console.error('❌ Error validating connector assignments:', error);
      throw new Error('Failed to validate connector assignments. Please check your connection and try again.');
    }
  };

  const startHubFlowStatusPolling = () => {
    if (statusPollIntervalRef.current) {
      return;
    }
    statusPollIntervalRef.current = setInterval(async () => {
      if (statusPollInFlightRef.current) {
        return;
      }
      statusPollInFlightRef.current = true;
      try {
        await fetchHubFlows({ silent: true });
      } finally {
        statusPollInFlightRef.current = false;
      }
    }, 2000);
  };

  const isDatabaseSyncHubFlow = (hubFlow) => {
    const normalize = (str) => {
      if (!str) return '';
      return str.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    };
    const cat = normalize(hubFlow?.sfiCategory);
    const sub = normalize(hubFlow?.sfiSubCategory);
    return (
      cat.includes('databasesync') ||
      cat.includes('dbsync') ||
      cat.includes('schemasync') ||
      cat.includes('postgrestosnowflake') ||
      sub.includes('postgrestosnowflake') ||
      (sub.includes('postgres') && sub.includes('snowflake'))
    );
  };

  const executeHubFlowRun = async (hubFlow) => {
    let runRequestSent = false;
    try {
      setExecutingFlow(true);
      setRunningFlowIds((prev) => new Set([...prev, hubFlow.instanceId]));
      setHubFlows((prev) =>
        prev.map((flow) =>
          flow.instanceId === hubFlow.instanceId ? { ...flow, status: 'RUNNING' } : flow
        )
      );
      previousFlowStatusesRef.current.set(hubFlow.instanceId, 'Running');

      const runUrl = tenantId
        ? `${HUB_FLOW_API_URL}/${hubFlow.instanceId}/run?tenantId=${tenantId}`
        : `${HUB_FLOW_API_URL}/${hubFlow.instanceId}/run`;
      runRequestSent = true;
      const response = await fetch(runUrl, {
        method: 'POST',
        headers: withBrowserTimezoneHeaders({
          Authorization: `Bearer ${token}`,
        }),
      });

      const runBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        const runError = new Error(runBody.message || `HTTP error! status: ${response.status}`);
        runError.confirmedFailure = ![502, 503, 504].includes(response.status);
        throw runError;
      }

      if (runBody.status === 'error') {
        const runError = new Error(
          runBody.message ||
            'Hub Flow could not start — check Data Hub APIs use the same Master Flow as the Hub Flow instance.'
        );
        runError.confirmedFailure = true;
        throw runError;
      }

      startHubFlowStatusPolling();
      await fetchHubFlows();
    } catch (error) {
      console.error('Run error:', error);

      const errorMessage = error?.message || '';
      const lowerErrorMessage = errorMessage.toLowerCase();
      const confirmedFailure = !runRequestSent || error?.confirmedFailure === true;

      if (!confirmedFailure) {
        startHubFlowStatusPolling();
        await fetchHubFlows();
        return;
      }

      if (lowerErrorMessage.includes('not connected') || lowerErrorMessage.includes('disconnected')) {
        toast.error(
          lowerErrorMessage.includes('assigned but disconnected')
            ? errorMessage
            : 'The assigned connector is Disconnected. Please reconnect it in the Connector Token screen before running this flow.'
        );
      } else if (
        errorMessage &&
        ((lowerErrorMessage.includes('assign') && lowerErrorMessage.includes('connector')) ||
          lowerErrorMessage.includes('connector not assigned'))
      ) {
        toast.error(errorMessage);
      } else if (errorMessage) {
        toast.error(`Error running hub flow: ${errorMessage}`);
      } else {
        toast.error('Error running hub flow');
      }

      setRunningFlowIds((prev) => {
        const next = new Set(prev);
        next.delete(hubFlow.instanceId);
        return next;
      });
      setExecutingFlow(false);
      setHubFlows((prev) =>
        prev.map((flow) =>
          flow.instanceId === hubFlow.instanceId ? { ...flow, status: 'ERROR' } : flow
        )
      );
    }
  };

  const fetchSchemaCompareReport = async (hubFlow) => {
    const compareUrl = tenantId
      ? `${HUB_FLOW_API_URL}/${hubFlow.instanceId}/schema-compare?tenantId=${tenantId}`
      : `${HUB_FLOW_API_URL}/${hubFlow.instanceId}/schema-compare`;
    const compareRes = await fetch(compareUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const report = await compareRes.json().catch(() => ({}));
    if (!compareRes.ok) {
      throw new Error(report.message || report.detail || `Schema compare failed (${compareRes.status})`);
    }
    if (report.status === 'error') {
      throw new Error(report.message || 'Schema comparison failed');
    }
    if (report.status === 'skipped' || report.isDatabaseSync === false) {
      throw new Error(report.message || 'Schema compare is only for Database Sync (Postgres → Snowflake) flows');
    }
    return report;
  };

  const handleCompareSchema = async (hubFlow) => {
    if (!hubFlow?.instanceId) {
      toast.error('Hub Flow instance not found');
      return;
    }
    if (!isDatabaseSyncHubFlow(hubFlow)) {
      toast.info('Schema compare is only available for Database Sync (Postgres → Snowflake) flows');
      return;
    }
    setSchemaCompareLoading(true);
    try {
      await validateHubFlowConnectors(hubFlow);
      const report = await fetchSchemaCompareReport(hubFlow);
      setSchemaCompareReport(report);
      setPendingRunFlow(null);
      setSchemaCompareMode('view');
      setSchemaCompareOpen(true);
    } catch (error) {
      console.error('Schema compare error:', error);
      toast.error(error?.message || 'Schema comparison failed');
    } finally {
      setSchemaCompareLoading(false);
    }
  };

  const handleRunHubFlow = async (flowId) => {
    try {
      const hubFlow = hubFlows.find((flow) => flow.sfl_id.toString() === flowId.toString());
      if (!hubFlow || !hubFlow.instanceId) {
        throw new Error('Hub Flow instance not found');
      }

      if (isFlowRunning(hubFlow.instanceId)) {
        toast.warning('This flow is already running.');
        return;
      }

      await validateHubFlowConnectors(hubFlow);
      // Schema compare is a separate menu action — Run starts sync only
      await executeHubFlowRun(hubFlow);
    } catch (error) {
      console.error('Run validation error:', error);
      const errorMessage = error?.message || '';
      if (errorMessage) {
        toast.error(errorMessage.startsWith('Error') ? errorMessage : `Error running hub flow: ${errorMessage}`);
      }
      setExecutingFlow(false);
    }
  };

  const handleConfirmSchemaCompareAndRun = async () => {
    const hubFlow = pendingRunFlow;
    setSchemaCompareOpen(false);
    setSchemaCompareReport(null);
    setPendingRunFlow(null);
    setSchemaCompareMode('view');
    if (hubFlow) {
      await executeHubFlowRun(hubFlow);
    }
  };

  const handleCancelSchemaCompare = () => {
    setSchemaCompareOpen(false);
    setSchemaCompareReport(null);
    setPendingRunFlow(null);
    setSchemaCompareMode('view');
    setExecutingFlow(false);
  };

  const handleOpenDetail = (flow) => {
    setSelectedFlowDetails(flow);
    setParamDrawerOpen(true);
  };

  const handleCloseParamDrawer = () => {
    setParamDrawerOpen(false);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleTriggerExecutorFlow = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/agent/executor/tenant/${tenantId}/process?includeAllPending=true`,
        {
          method: 'POST',
          headers: withBrowserTimezoneHeaders({
            'Authorization': `Bearer ${token}`,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success(data.message || 'Executor flow triggered successfully');
    } catch (error) {
      console.error('Error triggering executor flow:', error);
      toast.error(`Error triggering executor flow: ${error.message}`);
    }
  };

  const handleMenuItemClick = (action) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case 'Manual Run':
        if (!allowExecute) {
          toast.error('No Execute permission for Hub Flow');
          break;
        }
        handleRunHubFlow(selectedRowForMenu.sfl_id);
        break;
      case 'Compare Schema':
        handleCompareSchema(selectedRowForMenu);
        break;
      case 'Properties':
        handleOpenDetail(selectedRowForMenu);
        break;
      case 'Datahub Track Log':
        setSelectedFlowForTrackLog(selectedRowForMenu);
        setTrackLogModalOpen(true);
        break;
      case 'Run Agent Executor':
        if (!allowExecute) {
          toast.error('No Execute permission for Hub Flow');
          break;
        }
        handleTriggerExecutorFlow();
        break;
      default:
        break;
    }

    handleMenuClose();
  };

  const handleCloseTrackLogModal = () => {
    setTrackLogModalOpen(false);
    setSelectedFlowForTrackLog(null);
  };

  // Check if a specific flow is running
  const isFlowRunning = (instanceId) => {
    if (!instanceId) return false;

    // First check the actual status from hubFlows (most reliable)
    const flow = hubFlows.find(flow => flow.instanceId === instanceId);
    if (flow) {
      const status = flow.status?.toLowerCase();
      // If status is terminal, it's definitely not running
      if (
        status === 'completed' ||
        status === 'success' ||
        status === 'failed' ||
        status === 'error' ||
        status === 'success with errors'
      ) {
        return false;
      }
      // If status is explicitly "Running", it's running
      if (status === 'running') {
        return true;
      }
    }

    // Fallback: Check if it's in runningFlowIds (but only if status is not completed)
    // This handles the case where status hasn't been updated yet but we know it's running
    if (runningFlowIds.has(instanceId)) {
      // Only return true if status is not terminal
      if (flow) {
        const status = flow.status?.toLowerCase();
        if (
          status !== 'completed' &&
          status !== 'success' &&
          status !== 'failed' &&
          status !== 'error' &&
          status !== 'success with errors'
        ) {
          return true;
        }
      } else {
        // If flow not found yet, assume it's running if in runningFlowIds
        return true;
      }
    }

    return false;
  };

  const playButtonColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    renderCell: (params) => {
      const isRunning = isFlowRunning(params.row.instanceId);
      // Only disable if THIS specific flow is running, not if other flows are executing
      // This allows the button to enable immediately when this flow completes
      const isDisabled = isRunning || !allowExecute;

      return (
        <Tooltip title={!allowExecute ? 'No Execute permission' : isRunning ? 'Hub Flow is running...' : 'Run Hub Flow'}>
          <span>
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                if (!allowExecute) {
                  toast.error('No Execute permission for Hub Flow');
                  return;
                }
                handleRunHubFlow(params.row.sfl_id);
              }}
              disabled={isDisabled}
              size="small"
            >
              {isRunning ? <CircularProgress size={18} /> : <PlayArrow />}
            </IconButton>
          </span>
        </Tooltip>
      );
    },
  };

  const moreOptionsColumn = {
    field: 'moreOptions',
    headerName: '',
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchorEl(e.currentTarget);
            setSelectedRowForMenu(params.row);
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      );
    },
  };

  const columns = [
    {
      field: 'flowName',
      headerName: 'Flow',
      width: 200,
      valueGetter: (_value, row) => getMasterFlowNameById(row?.sfiMfId) || row?.sfl_description || '',
    },
    { field: 'sfl_description', headerName: 'Description', width: 250 },
    { field: 'sfiCategory', headerName: 'Category', width: 150 },
    { field: 'sfiSubCategory', headerName: 'Sub-Category', width: 180 },
    {
      field: 'sfiTriggerType',
      headerName: 'Trigger Type',
      width: 160,
      valueGetter: (value, row) => {
        const triggerType = row?.sfiTriggerType || 'Manual';
        if (triggerType === 'Minutes' && row?.sfiInterval) {
          return `Minutes (every ${row.sfiInterval} min)`;
        }
        if (triggerType === 'Time' && row?.sfiTime) {
          return `Time (${row.sfiTime})`;
        }
        return triggerType;
      },
    },
    { field: 'status', headerName: 'Status', width: 120 },
    playButtonColumn,
    moreOptionsColumn,
  ];

  const formFields = [
    {
      name: 'flow',
      label: 'Flow',
      required: true,
      type: 'select',
      width: 6,
      options: masterFlowOptions.map((name) => ({ value: name, label: name })),
      validate: (value) => (!value ? 'Flow is required' : null),
    },
    {
      name: 'targetAgent',
      label: 'Target Agent',
      required: false,
      type: 'select',
      width: 6,
      show: (formData) => isPlannerFlowByName(formData?.flow),
      options: buildTargetAgentOptions(),
      helperText: 'All Agents materializes tasks for every agent. Select one agent to scope the planner.',
    },
    {
      name: 'sourceConnector',
      label: 'Source Connector',
      required: (formData) => !isAgentFlowByName(formData?.flow),
      type: 'select',
      options: sourceConnectorOptions,
      onChange: (value, formData) => {
        // Track source connector type for category validation
        const selectedConnectorOption = sourceConnectorOptions.find(
          opt => opt.value === value || opt.value?.toString() === value?.toString()
        );
        if (selectedConnectorOption && selectedConnectorOption.mcName) {
          setSelectedSourceConnectorType(selectedConnectorOption.mcName);

          // Validate category if already selected
          const currentCategory = formData?.category || selectedCategory;
          if (currentCategory && currentCategory !== 'All' && currentCategory !== '') {
            validateCategoryAgainstSourceConnector(currentCategory, value);
          }
        } else {
          setSelectedSourceConnectorType('');
        }
      },
    },
    {
      name: 'targetConnector',
      label: 'Target Connector',
      required: (formData) => !isAgentFlowByName(formData?.flow),
      type: 'select',
      options: targetConnectorOptions,
    },
    {
      name: 'sfiTriggerType',
      label: 'Trigger Type',
      required: true,
      type: 'custom',
      component: TriggerTypeSelector,
    },
    {
      name: 'category',
      label: 'Category',
      required: false,
      type: 'select',
      width: 6,
      options: [
        { value: '', label: 'All' },
        ...categories.map((cat) => ({ value: cat, label: cat })),
      ],
      onCategoryChange: (category, formData) => {
        setSelectedCategory(category || '');

        // Validate category against source connector if source connector is selected
        if (category && category !== 'All' && category !== '' && formData?.sourceConnector) {
          validateCategoryAgainstSourceConnector(category, formData.sourceConnector);
        }
      },
    },
    {
      name: 'subCategory',
      label: 'Sub-Category',
      required: false,
      type: 'select',
      width: 6,
      options: [
        { value: '', label: 'All' },
        ...subCategories.map((subCat) => ({ value: subCat, label: subCat })),
      ],
      disabled: !selectedCategory,
    },
    {
      name: 'sfl_description',
      label: 'Description',
      multiline: true,
      rows: 3,
      width: 12,
      helperText: 'Optional. Leave blank to use the selected flow name.',
    },
  ];

  const initialFormData = {
    flow: '',
    targetAgent: '',
    sourceConnector: '',
    targetConnector: '', // Will be set from available PostgreSQL connectors
    sfiTriggerType: { triggerType: 'Manual', intervalValue: '', timeValue: '' },
    category: '',
    subCategory: '',
    sfl_description: '',
  };

  const prepareEditData = async (row) => {
    // Get master flow name from sfiMfId (stored in the row data)
    // Convert to string for comparison if needed
    const masterFlowId = row.sfiMfId ? (typeof row.sfiMfId === 'string' ? row.sfiMfId : row.sfiMfId.toString()) : null;
    const masterFlowName = masterFlowId ? getMasterFlowNameById(masterFlowId) : '';

    console.log('prepareEditData - row.sfiMfId:', row.sfiMfId, 'masterFlowName:', masterFlowName);

    let sourceConnectorValue = '';
    if (row.sfiSourceConnectorId) {
      const savedSourceId = row.sfiSourceConnectorId.toString();
      const masterOption = sourceConnectorOptions.find((opt) => opt.value === savedSourceId);
      if (masterOption) {
        sourceConnectorValue = savedSourceId;
      } else {
        const sourceType = await resolveMasterConnectorType(savedSourceId);
        const sourceMasterOption = sourceConnectorOptions.find((opt) => opt.mcName === sourceType);
        sourceConnectorValue = sourceMasterOption?.value || '';
      }
      console.log('prepareEditData - Source connector value:', sourceConnectorValue);
    } else {
      // Fallback: Try to fetch from connector assignments (for backward compatibility)
      // But use flowInstanceId instead of masterFlowId for Hub Flows
      const flowIdToUse = row.instanceId || masterFlowId;
      if (flowIdToUse) {
        try {
          const connectorResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-connector-assignment/flow/${flowIdToUse}`,
            {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (connectorResponse.ok) {
            const connectorData = await connectorResponse.json();
            // Find the source connector (exclude Postgres)
            if (Array.isArray(connectorData) && connectorData.length > 0) {
              const sourceConnectorAssignment = connectorData.find(
                (assignment) => assignment.connectorType !== 'Postgres'
              );

              if (sourceConnectorAssignment) {
                // Match by connectorId (value)
                const connectorOption = sourceConnectorOptions.find(
                  (opt) => opt.value === sourceConnectorAssignment.connectorId ||
                    opt.value?.toString() === sourceConnectorAssignment.connectorId?.toString()
                );
                sourceConnectorValue = connectorOption ? connectorOption.value : sourceConnectorAssignment.connectorId;
                console.log('prepareEditData - Found connector from assignments:', sourceConnectorValue);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching assigned connectors for edit:', error);
          // Continue without populating connector - user can select manually
        }
      }
    }

    // Set category and fetch sub-categories when editing
    if (row.sfiCategory) {
      setSelectedCategory(row.sfiCategory);
    } else {
      setSelectedCategory('');
    }

    let targetConnectorValue = '';
    if (row.sfiTargetConnectorId) {
      const savedTargetId = row.sfiTargetConnectorId.toString();
      const masterOption = targetConnectorOptions.find((opt) => opt.value === savedTargetId);
      if (masterOption) {
        targetConnectorValue = savedTargetId;
      } else {
        const targetType = await resolveMasterConnectorType(savedTargetId);
        const targetMasterOption = targetConnectorOptions.find((opt) => opt.mcName === targetType);
        targetConnectorValue = targetMasterOption?.value || '';
      }
      console.log('prepareEditData - Target connector value:', targetConnectorValue);
    }

    let targetAgent = '';
    if (masterFlowId && isPlannerFlowByName(masterFlowName) && row.instanceId) {
      targetAgent = await loadTargetAgentProperty(masterFlowId, row.instanceId);
    }

    return {
      ...row,
      flow: masterFlowName, // Set selected flow to master flow name
      targetAgent,
      sourceConnector: sourceConnectorValue, // Populate with assigned connector
      targetConnector: targetConnectorValue, // Use saved target connector ID
      sfiTriggerType: {
        triggerType: row.sfiTriggerType || 'Manual',
        intervalValue: row.sfiInterval?.toString() || '',
        timeValue: row.sfiTime || '',
      },
      category: row.sfiCategory || '',
      subCategory: row.sfiSubCategory || '',
      sfl_description: row.sfl_description || '',
    };
  };

  const breadcrumbPaths = generateBreadcrumbPath('Hub Flow');

  return (
    <>
      <DynamicContainer
        title="Hub Flow"
        icon={Sync}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="sfl_id"
        breadcrumbPaths={breadcrumbPaths}
        rows={hubFlows}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        prepareEditData={prepareEditData}
        isLoading={isLoading}
        functionCode="Hub Flow"
      />
      {selectedFlowDetails && (
        <HubFlowParameter
          open={paramDrawerOpen}
          onClose={handleCloseParamDrawer}
          masterFlowId={selectedFlowDetails.sfiMfId}
          flowInstanceId={selectedFlowDetails.instanceId}
          masterFlowName={getMasterFlowNameById(selectedFlowDetails.sfiMfId) || selectedFlowDetails.sfl_description || ''}
          sfiSourceConnectorId={selectedFlowDetails.sfiSourceConnectorId}
          sfiTargetConnectorId={selectedFlowDetails.sfiTargetConnectorId}
          isHubFlow={true}
          uiConfig={uiConfigForMasterFlow(masterFlowsData, selectedFlowDetails.sfiMfId, {
            hasFullAccess,
            licensedFlowUiByMfId,
          })}
        />
      )}

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {(() => {
          const menuUi = uiConfigForMasterFlow(masterFlowsData, selectedRowForMenu?.sfiMfId, {
            hasFullAccess,
            licensedFlowUiByMfId,
          });
          return (
            <>
              {isMfUiActionEnabled(menuUi, 'manualRun', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Manual Run')} disabled={!allowExecute}>
                  <ListItemText primary={allowExecute ? 'Manual Run' : 'Manual Run (no Execute)'} />
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'compareSchema', { hasFullAccess }) && (
                  <MenuItem onClick={() => handleMenuItemClick('Compare Schema')}>
                    <ListItemText primary="Compare Schema" />
                  </MenuItem>
                )}
              {isMfUiActionEnabled(menuUi, 'runAgentExecutor', { hasFullAccess }) && (
                <MenuItem
                  onClick={() => handleMenuItemClick('Run Agent Executor')}
                  disabled={!allowExecute}
                >
                  <ListItemText
                    primary={allowExecute ? 'Run Agent Executor' : 'Run Agent Executor (no Execute)'}
                  />
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'properties', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Properties')}>
                  <ListItemText primary="Properties" />
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'datahubTrackLog', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Datahub Track Log')}>
                  <ListItemText primary="Datahub Track Log" />
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      <DatahubTrackLogModal
        open={trackLogModalOpen}
        onClose={handleCloseTrackLogModal}
        flowData={selectedFlowForTrackLog}
        isFlowRunning={selectedFlowForTrackLog ? isFlowRunning(selectedFlowForTrackLog.instanceId) : false}
      />

      <Dialog
        open={schemaCompareOpen}
        onClose={handleCancelSchemaCompare}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Schema comparison (Postgres → Snowflake)</DialogTitle>
        <DialogContent dividers>
          {schemaCompareReport && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Alert
                severity={
                  schemaCompareReport.hasBlockingDifferences
                    ? 'error'
                    : schemaCompareReport.status === 'ok'
                      ? 'success'
                      : 'warning'
                }
              >
                {schemaCompareReport.message}
                {schemaCompareReport.policyNote ? ` ${schemaCompareReport.policyNote}` : ''}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Source: <strong>{schemaCompareReport.sourceDatabase}</strong>
                {' → '}
                Target: <strong>
                  {schemaCompareReport.targetDatabase}.{schemaCompareReport.targetSchema}
                </strong>
                {' · '}
                SchemaPolicy: <strong>{schemaCompareReport.schemaPolicy}</strong>
                {' · '}
                SyncMode: <strong>{schemaCompareReport.syncMode}</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`Tables: ${schemaCompareReport.summary?.tablesChecked ?? 0}`}
                />
                <Chip
                  size="small"
                  color="warning"
                  label={`Missing tables: ${schemaCompareReport.summary?.missingTables ?? 0}`}
                />
                <Chip
                  size="small"
                  color="warning"
                  label={`Missing columns: ${schemaCompareReport.summary?.missingColumns ?? 0}`}
                />
                <Chip
                  size="small"
                  color="error"
                  label={`Type mismatches: ${schemaCompareReport.summary?.typeMismatches ?? 0}`}
                />
              </Box>
              <Typography variant="subtitle2">Tables</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source table</TableCell>
                    <TableCell>Target table</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(schemaCompareReport.tables || schemaCompareReport.differences || []).map((diff) => {
                    const missingCols = (diff.missingColumns || [])
                      .map((c) => (typeof c === 'string' ? c : c.column))
                      .join(', ');
                    const typeIssues = (diff.typeMismatches || [])
                      .map(
                        (tm) =>
                          `${tm.column}: PG ${tm.sourceType} → SF ${tm.targetType} (want ${tm.mappedSnowflakeType})`
                      )
                      .join('; ');
                    let details = 'Matched';
                    if (diff.missingTable) {
                      details = 'Table missing on Snowflake';
                    } else {
                      const parts = [];
                      if (missingCols) parts.push(`Missing columns: ${missingCols}`);
                      if (typeIssues) parts.push(`Type issues: ${typeIssues}`);
                      if (parts.length) details = parts.join(' · ');
                      else if (diff.matchedColumns != null) {
                        details = `${diff.matchedColumns} column(s) matched`;
                      }
                    }
                    return (
                      <TableRow key={diff.sourceTable || diff.targetTable}>
                        <TableCell>{diff.sourceTable}</TableCell>
                        <TableCell>{diff.targetTable}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={
                              diff.status === 'OK'
                                ? 'success'
                                : diff.status === 'MISSING_TABLE'
                                  ? 'warning'
                                  : 'error'
                            }
                            label={diff.status || '—'}
                          />
                        </TableCell>
                        <TableCell>{details}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!(schemaCompareReport.tables || schemaCompareReport.differences || []).length && (
                    <TableRow>
                      <TableCell colSpan={4}>No tables discovered in source database.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSchemaCompare}>Close</Button>
          {schemaCompareMode === 'confirm-run' && (
            <Button
              variant="contained"
              color={schemaCompareReport?.hasBlockingDifferences ? 'warning' : 'primary'}
              onClick={handleConfirmSchemaCompareAndRun}
            >
              Proceed with sync
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {schemaCompareLoading && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1400,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 3,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2">Comparing schemas…</Typography>
        </Box>
      )}
    </>
  );
};

export default HubFlow;

